import React from "react";
import { Link } from "react-router-dom";
import { CARD_CLASS, cardStyle, MICRO_LABEL, chipClass, type ChipTone } from "./index";

/**
 * "Owed action today" — the single ranked index of what needs the CIO, across every
 * domain the app covers.
 *
 * WHY THIS EXISTS. Before this, /portfolio answered "what should I do" in six places
 * at once (a NAV action badge, a prose playbook, a Δ-vs-target chip, a sleeve tile, a
 * trim preview, an exit queue, a harvest list and a staged-orders block) with nothing
 * saying which was authoritative. This replaces that with one list: what, how many,
 * and where it is handled.
 *
 * THE RULES, which are what make it trustworthy — do not relax them:
 *
 *  1. COUNTS AND VERDICTS ONLY. Never a dollar amount, never a percentage. The moment a
 *     figure appears here it becomes a second place that figure lives, and the pages
 *     this replaces got into trouble precisely that way. Detail belongs in `detail`,
 *     which is secondary text, and the authoritative number belongs on `handledAt`.
 *  2. EVERY ROW NAMES WHERE IT IS HANDLED. A row that tells you something is wrong but
 *     not where to deal with it is how the old panel stranded people on a page that
 *     could not action anything.
 *  3. A REAL RESTING STATE. When nothing is owed, this says so plainly and still lists
 *     what it checked, so "quiet" is provably "checked and clear" rather than "failed
 *     to load". A panel that cannot go quiet gets ignored, which is the failure mode
 *     that made the old attention panel invisible.
 *  4. NOTHING ACTIONABLE MAY LIVE ONLY BEHIND A COLLAPSED SECTION. Once the page has
 *     drawers, every non-zero drawer badge must have a matching row here.
 *
 * PLACEMENT. This is deliberately a standalone shared component rather than page-local
 * markup: the CIO decision of 2026-08-27 was Portfolio-first, with promotion into the
 * app header once it is proven to stay quiet on ordinary days. That promotion should be
 * a move of this element, not a rewrite.
 *
 * Display only. Nothing here transmits an order or mutates state.
 */

export type OwedActionRow = {
  /** Stable key. */
  id: string;
  /** What it is, in the CIO's words. */
  label: string;
  /** A count or a verdict. NEVER a dollar amount or a percentage — see rule 1. */
  verdict: string;
  tone: ChipTone;
  /** True when this row is asking for something today. Drives ordering and the resting state. */
  needsAction: boolean;
  /** Where the CIO deals with it: a page name, or "below" for something on this page. */
  handledAt: string;
  /** Route to jump to, when it is handled on another page. */
  href?: string;
  /** In-page target (a drawer). Takes precedence over href. */
  onSelect?: () => void;
  /** Secondary line. Still no dollars or percentages. */
  detail?: string;
};

const HandoffLink: React.FC<{ row: OwedActionRow }> = ({ row }) => {
  const text = row.handledAt;
  const cls =
    "shrink-0 text-[11px] underline-offset-2 hover:underline focus-visible:underline " +
    "focus-visible:outline-none";

  if (row.onSelect) {
    return (
      <button type="button" onClick={row.onSelect} className={cls} style={{ color: "var(--accent-info)" }}>
        {text} →
      </button>
    );
  }
  if (row.href) {
    return (
      <Link to={row.href} className={cls} style={{ color: "var(--accent-info)" }}>
        {text} →
      </Link>
    );
  }
  return (
    <span className="shrink-0 text-[11px]" style={{ color: "var(--text-muted)" }}>
      {text}
    </span>
  );
};

const Row: React.FC<{ row: OwedActionRow }> = ({ row }) => (
  <div
    className="flex items-center justify-between gap-3 rounded-lg px-2.5 py-2"
    style={{ backgroundColor: "var(--table-row-alt-bg)", border: "1px solid var(--card-border)" }}
  >
    <div className="min-w-0">
      <div className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
        {row.label}
      </div>
      {row.detail ? (
        <div className="truncate text-[10px]" style={{ color: "var(--text-muted)" }} title={row.detail}>
          {row.detail}
        </div>
      ) : null}
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <span className={chipClass(row.tone)}>{row.verdict}</span>
      <HandoffLink row={row} />
    </div>
  </div>
);

export const OwedActionPanel: React.FC<{
  rows: OwedActionRow[];
  /** Shown when the page has not loaded yet, so "quiet" is never confused with "unknown". */
  loading?: boolean;
  className?: string;
}> = ({ rows, loading = false, className = "" }) => {
  const owed = rows.filter((r) => r.needsAction);
  const clear = rows.filter((r) => !r.needsAction);

  return (
    <div className={`${CARD_CLASS} ${className}`.trim()} style={cardStyle}>
      <div className="flex items-center justify-between gap-2">
        <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
          Owed action today
        </div>
        <span className={chipClass(loading ? "neutral" : owed.length ? "warning" : "positive")}>
          {loading ? "Checking" : owed.length ? `${owed.length} open` : "All clear"}
        </span>
      </div>

      {loading ? (
        <div className="mt-2.5 text-xs" style={{ color: "var(--text-muted)" }}>
          Checking allocation, exits, harvest, staged tickets and data freshness…
        </div>
      ) : owed.length === 0 ? (
        <div className="mt-2.5">
          <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
            Nothing needs you today.
          </div>
          {/* Name what was checked, so a quiet panel reads as "checked and clear"
              rather than "nothing loaded". */}
          <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Checked: {clear.map((r) => `${r.label.toLowerCase()} (${r.verdict.toLowerCase()})`).join(" · ")}.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-2.5 grid gap-1.5">
            {owed.map((r) => (
              <Row key={r.id} row={r} />
            ))}
          </div>
          {clear.length > 0 ? (
            <div className="mt-2 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Clear: {clear.map((r) => `${r.label.toLowerCase()} (${r.verdict.toLowerCase()})`).join(" · ")}.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default OwedActionPanel;
